// Phone helpers shared by the admin booking modal and server-side validation.
// SSR-safe (no DOM).

const PHONE_ALLOWED = /^[\d+\-\s()]+$/;
const PHONE_MAX_LEN = 32;
const PHONE_MIN_DIGITS = 7;

// Unicode dashes/hyphens that can sneak in via copy-paste (Russian operators
// often format numbers with U+2011 non-breaking hyphen — e.g. "+7 903 790‑23‑24").
// Replace them with ASCII '-' so the rest of the pipeline (regex shape check,
// tel: link, DB storage) sees a canonical form.
//   U+2010 hyphen, U+2011 non-breaking, U+2012 figure dash, U+2013 en dash,
//   U+2014 em dash, U+2015 horizontal bar, U+2212 minus sign.
const UNICODE_DASH_RE = /[‐‑‒–—―−]/g;

// Bidi/zero-width formatting characters that iOS/Android paste-buffers wrap
// phone numbers with (LRE/RLE/PDF/LRI/RLI/PDI/LRM/RLM/zero-width
// spaces/joiners + BOM). Invisible — they break startsWith('+') checks and
// the shape regex without showing in the UI.
const INVISIBLE_FORMAT_RE = /[\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF]/g;

export function digitsOnly(input: string | null | undefined): string {
  return (input ?? '').replace(/\D/g, '');
}

// International value for the digits-only "+" prefix field (PhoneField.vue):
// keep only digits and prepend a single '+'. Returns '' when there are no
// digits. Used to derive the submitted phone from raw typed/pasted input —
// strips letters, separators and stray '+' in one pass.
export function toIntlDigits(input: string | null | undefined): string {
  const d = digitsOnly(input);
  return d ? '+' + d : '';
}

// Canonicalize a phone string: strip invisible bidi/zero-width markers,
// replace Unicode dashes with ASCII '-', trim, collapse runs of leading '+'
// to a single one, and prepend '+' when the input has digits but no leading
// '+' (international format is the project convention). Returns '' when the
// input has no digits at all.
export function normalizePhone(input: string | null | undefined): string {
  if (input == null) return '';
  let s = input.replace(INVISIBLE_FORMAT_RE, '').replace(UNICODE_DASH_RE, '-').trim();
  if (s.length === 0) return '';
  if (!/\d/.test(s)) return s; // nothing to normalize, leave as-is for shape validator to reject
  s = s.replace(/^\++/, '+'); // collapse '++7…' (e.g. when bidi markers tricked an earlier pass)
  if (!s.startsWith('+')) s = '+' + s;
  return s;
}

export function isPhoneShapeValid(input: string): boolean {
  const trimmed = input.trim();
  if (trimmed.length === 0 || trimmed.length > PHONE_MAX_LEN) return false;
  return PHONE_ALLOWED.test(trimmed);
}

export function hasEnoughPhoneDigits(input: string | null | undefined): boolean {
  return digitsOnly(input).length >= PHONE_MIN_DIGITS;
}

// Canonical bare-digits form for matching/grouping ("+7 926 586-50-66" ≡ "+79265865066").
// Anything under 8 digits is too ambiguous to match on → null. Mirrors the DB
// column bookings.phone_digits (kept in sync by services/admin-bookings.ts).
export const PHONE_MATCH_MIN_DIGITS = 8;
export function phoneDigits(phone: string | null | undefined): string | null {
  const d = digitsOnly(phone);
  return d.length >= PHONE_MATCH_MIN_DIGITS ? d : null;
}
